import { inject } from "inversify";
import { BackendConfig } from "../config/BackendConfig";
import { serviceIdentifiers } from "../container-service-identifiers";
import { INavigationService } from "../core/navigation/INavigationService";

type AuthenticationStatus = "unauthenticated" | "authenticating" | "authenticated" | "error";

type TokenPair = {
    accessToken: string;
    refreshToken: string;
};

export default class AuthenticationInitiator {
    private _navigationService: INavigationService;
    private _tokenPair: TokenPair | null = null;
    private _status: AuthenticationStatus = "unauthenticated";
    private _isRedirecting: boolean = false;

    constructor(@inject(serviceIdentifiers.INavigationService) navigationService: INavigationService) {
        this._navigationService = navigationService;
    }

    async authenticate() {
        console.log(`Resolving API ticket...`);
        this._status = "authenticating";

        const apiTicketFragment = this._navigationService.getFragmentValue("apiTicket");
        if (!apiTicketFragment) {
            console.log(`No API ticket found in URL fragment. Redirecting to login...`);
            this.redirectToLogin();
            return;
        }

        console.log(`API ticket found: ${apiTicketFragment.substring(0, 10)}************** (truncated)`);
        this._navigationService.removeFragment("apiTicket");

        try {
            const rawResponse = await fetch(`${BackendConfig.backendRestApiAddress}/AccessTokens/Create`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({ apiTicket: apiTicketFragment }),
            });

            const response = await rawResponse.json();
            const accessToken = response.accessToken as string | null;
            const refreshToken = response.refreshToken as string | null;

            this._status = "authenticated";
            if (!accessToken || !refreshToken) {
                console.error(`Failed to exchange API ticket for access token. Tokens not found in response.`);
                this._status = "error";
                this.redirectToLogin();
                return;
            }

            // NOTE: The tokens are currently BASE64-encoded strings that contain '+' characters.
            // However, during URL fragment transmission, '+' characters may be replaced with spaces.
            // Therefore, we need to replace spaces back to '+' before using the tokens.
            // This is a workaround for URL encoding issues. We should really use Base64URL encoding for tokens to avoid such issues.
            this._tokenPair = {
                accessToken: accessToken.replace(/ /g, '+'),
                refreshToken: refreshToken.replace(/ /g, '+')
            };
            console.log(`Successfully exchanged API ticket for access token.`);
        } catch (ex) {
            console.error(`Error while exchanging API ticket for access token:`, ex);
            this._status = "error";
            this.redirectToLogin();
        }
    }

    redirectToLogin() {
        const loginUrl = BackendConfig.studentLoginAddress;
        this._isRedirecting = true;
        window.location.href = loginUrl;
    }

    get tokenPair(): TokenPair | null {
        return this._tokenPair;
    }

    get status(): AuthenticationStatus {
        return this._status;
    }

    get isWaiting(): boolean {
        return this._status === "unauthenticated" || this._status === "authenticating";
    }

    get isAuthenticated(): boolean {
        return this._status === "authenticated" && this._tokenPair !== null;
    }

    get isError(): boolean {
        return this._status === "error";
    }

    get isRedirecting(): boolean {
        return this._isRedirecting;
    }
}